// Card-id registry, grid constants, the read-only Default layout, and the
// merge-with-default helper for forward-compatible saved layouts.
//
// CARD_IDS, CardId, and DashboardLayoutItem are the shared FE contract and
// live in `@interfaces/IDashboardLayout`. They are imported for local use
// and re-exported here so all existing importers that read from
// `./layout.defaults` continue to work without change.
import { CARD_IDS, type CardId, type DashboardLayoutItem } from '@interfaces/IDashboardLayout';
import type { Feature } from '@d-orca/shared/permissions';
export { CARD_IDS };
export type { CardId, DashboardLayoutItem };

export const DEFAULT_LAYOUT_ID = 'default';

// 12-col grid with square cells: the row height is NOT a constant — it tracks
// the measured column width (computed in DashboardGrid) so every cell is a
// square. Only the column count and gutter are fixed here.
export const GRID_COLS = 12;
export const GRID_MARGIN: [number, number] = [14, 14];

const MIN_W = 3;
const MIN_H = 2;

// Square-cell grid: rowHeight tracks the column width (computed in DashboardGrid),
// so one row-unit ≈ one column-unit. `h` values below are in those square cells,
// chosen to reproduce each card's prior pixel height (e.g. a "tall" half-card was
// ~514px ≈ 5 square cells at the live column width).
//   row0: site-overview | workforce           (half, tall)
//   row1: morning-brief | project-progress    (half — the day's lede beside the
//         number it is usually about)
//   row2: master-schedule                     (full)
//   row3: activities | safety | delivery      (third, tall; delivery scrolls its
//         deliveries + inventory list within the shared cell height)
//   row4: weather                             (full, compact)
//   row5: ai-agent                            (full, compact)
//
// THIS ARRANGEMENT WAS LAID OUT BY HAND in the app and lifted back into code, so
// it is the shape somebody actually read a morning on rather than a guess — do
// not "tidy" it into even rows without doing the same.
//
// Row 1 used to be Project Progress alone across all twelve columns (Executive
// Summary, its former neighbour, was retired). The morning card took the empty
// half back: the written passage and the progress ring are the two things read
// first, and they are read against each other.
export const DEFAULT_LAYOUT: DashboardLayoutItem[] = [
  { i: 'visual-site-overview', x: 0, y: 0, w: 6, h: 5, minW: MIN_W, minH: MIN_H },
  { i: 'workforce', x: 6, y: 0, w: 6, h: 5, minW: MIN_W, minH: MIN_H },
  // minW 6: the written passage is sentences, and sentences in a quarter-width
  // column are a stack of two-word lines.
  { i: 'morning-brief', x: 0, y: 5, w: 6, h: 4, minW: 6, minH: MIN_H },
  // minW stays MIN_W (not the 6 the other half/full-width cards use): the card
  // works small, and raising the clamp would re-stamp a floor above the width
  // users have already saved for it.
  { i: 'project-progress', x: 6, y: 5, w: 6, h: 4, minW: MIN_W, minH: MIN_H },
  { i: 'master-schedule', x: 0, y: 9, w: 12, h: 4, minW: 6, minH: MIN_H },
  { i: 'activities', x: 0, y: 13, w: 4, h: 5, minW: MIN_W, minH: MIN_H },
  { i: 'safety', x: 4, y: 13, w: 4, h: 5, minW: MIN_W, minH: MIN_H },
  { i: 'delivery', x: 8, y: 13, w: 4, h: 5, minW: MIN_W, minH: MIN_H },
  { i: 'weather', x: 0, y: 18, w: 12, h: 4, minW: 6, minH: MIN_H },
  // minH 1 (not MIN_H): the collapsed input strip is the one card that can
  // meaningfully go down to a single cell.
  { i: 'ai-agent', x: 0, y: 22, w: 12, h: 2, minW: 6, minH: 1 },
];

/**
 * i18n key of each card's title, for surfaces that need to NAME a card rather
 * than render it — the card-visibility menu and the per-card hide control. Each
 * card owns its own title inside its component, so those surfaces have nowhere
 * else to read a label from.
 */
export const CARD_TITLE_KEYS: Record<CardId, string> = {
  'morning-brief': 'dashboard.morningBrief.title',
  'visual-site-overview': 'dashboard.visualSiteOverview.title',
  workforce: 'dashboard.workforce.title',
  'project-progress': 'dashboard.projectProgress.title',
  'master-schedule': 'dashboard.masterSchedule.title',
  activities: 'dashboard.activityAlerts.title',
  safety: 'dashboard.safetyCompliance.title',
  delivery: 'dashboard.delivery.title',
  weather: 'dashboard.weatherImpact.title',
  'ai-agent': 'dashboard.aiAgent.title',
};

/**
 * The feature a card needs before it is offered at all. A card that reads data
 * the role may not see is left off the grid and out of the Cards menu, rather
 * than rendered as an error — and its saved slot is kept untouched.
 */
export const CARD_FEATURES: Partial<Record<CardId, Feature>> = {
  safety: 'safety.view',
};

/** The layout items the user's features allow — the rest are not offered. */
export function offeredItems(layout: DashboardLayoutItem[], features: ReadonlySet<Feature>): DashboardLayoutItem[] {
  return layout.filter((item) => {
    const needed = CARD_FEATURES[item.i];
    return !needed || features.has(needed);
  });
}

const KNOWN = new Set<string>(CARD_IDS);

/**
 * Reconcile a saved layout against the default: drop unknown card ids, and add
 * any known card missing from the saved layout at its default slot. Keeps old
 * saved layouts working after cards are added/removed in later releases.
 *
 * A card the user has hidden stays in the saved layout carrying `hidden: true`,
 * so it is "kept" here and its flag survives untouched. Only genuinely missing
 * cards are re-added, and they arrive without the flag — i.e. visible.
 */
export function mergeWithDefault(saved: DashboardLayoutItem[]): DashboardLayoutItem[] {
  const kept = saved.filter((it) => KNOWN.has(it.i)).map((it) => {
    // x/y/w/h are the user's data; minW/minH are product policy. Re-stamp the
    // constraints from the current default so layouts saved under an older,
    // stricter clamp (e.g. ai-agent minH 2 → 1) don't stay frozen forever.
    const def = DEFAULT_LAYOUT.find((d) => d.i === it.i);
    return def ? { ...it, minW: def.minW, minH: def.minH } : it;
  });
  const present = new Set(kept.map((it) => it.i));
  const missing = DEFAULT_LAYOUT.filter((it) => !present.has(it.i));
  return [...kept, ...missing];
}
